import React, {useState} from 'react';
import {observer} from 'mobx-react-lite';
import {useNavigate} from 'react-router-dom';
import {Skeleton} from 'primereact/skeleton';
import appStore from '../../storage/AppStore';
import systemStore from '../../storage/SystemStore';
import clubStore from '../../storage/ClubStore';
import PageLayout from '../PageLayout';
import Btn from '../../ui/Btn';

const tileStyle = {
    cursor: 'pointer', border: '1px solid var(--app-border)', borderRadius: '16px',
    padding: '24px 16px', minHeight: '165px', background: 'var(--app-bg-2)',
    color: 'var(--app-text)', transition: 'all .2s ease'
};
const iconStyle = {
    width: '64px', height: '64px', borderRadius: '50%',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    background: 'var(--app-bg)', color: 'var(--primary-color)'
};

const PageSkeleton = () => (
    <PageLayout header={<Skeleton width="14rem" height="2rem" borderRadius="10px"/>}>
        <div className="w-full mx-auto" style={{maxWidth: '1050px'}}>
            <div className="flex flex-column align-items-center gap-2 py-4 mb-3">
                <Skeleton shape="circle" size="4rem"/>
                <Skeleton width="15rem" height="1.5rem" borderRadius="8px"/>
                <Skeleton width="min(90%, 22rem)" height="1rem" borderRadius="8px"/>
            </div>
            <div className="grid">
                {Array.from({length: 6}, (_, i) => (
                    <div key={i} className="col-12 sm:col-6 lg:col-4">
                        <div className="flex flex-column align-items-center justify-content-center gap-3 shadow-1" style={{...tileStyle, cursor: 'default'}}>
                            <Skeleton shape="circle" size="4rem"/>
                            <Skeleton width="75%" height="1.2rem" borderRadius="8px"/>
                            <Skeleton width="45%" height="0.85rem" borderRadius="8px"/>
                        </div>
                    </div>
                ))}
            </div>
        </div>
    </PageLayout>
);

const NotFound = () => {
    const navigate = useNavigate();
    const [selectingId, setSelectingId] = useState(null);
    const companies = appStore.user?.companies || [];
    const isLoading = !appStore.user?.initialized || systemStore.loading;
    const needsClub = !clubStore.club.id && companies.length > 0;

    const selectClub = async (companyId) => {
        if (selectingId !== null) return;
        setSelectingId(companyId);
        try {
            await clubStore.getClub(companyId);
        } catch (error) {
            console.error('--club selection error', error);
        } finally {
            setSelectingId(null);
        }
    };

    if (isLoading) return <PageSkeleton/>;

    if (needsClub) {
        return (
            <PageLayout header={<h3 className="m-0">{appStore.tr.select_club}</h3>}>
                <div className="w-full mx-auto" style={{maxWidth: '1050px'}}>
                    <div className="text-center py-4 mb-3">
                        <div className="flex align-items-center justify-content-center mx-auto mb-3" style={iconStyle}>
                            <i className="pi pi-building" style={{fontSize: '1.8rem'}}/>
                        </div>
                        <h3 className="m-0 mb-2">{appStore.tr.select_club}</h3>
                        <div className="text-color-secondary">
                            {appStore.tr.select_club_hint}
                        </div>
                    </div>
                    <div className="grid">
                        {companies.map(({company}) => company && (
                            <div key={company.id} className="col-12 sm:col-6 lg:col-4 px-4">
                                <button type="button" disabled={selectingId !== null}
                                        className="w-full h-full flex flex-column align-items-center justify-content-center text-center gap-3 shadow-1"
                                        style={tileStyle} onClick={() => selectClub(company.id)}
                                        onMouseEnter={(e) => {
                                            if (selectingId !== null) return;
                                            e.currentTarget.style.transform = 'translateY(-4px)';
                                            e.currentTarget.style.boxShadow = '0 8px 24px rgba(0,0,0,.12)';
                                            e.currentTarget.style.borderColor = 'var(--primary-color)';
                                        }}
                                        onMouseLeave={(e) => {
                                            e.currentTarget.style.transform = '';
                                            e.currentTarget.style.boxShadow = '';
                                            e.currentTarget.style.borderColor = 'var(--app-border)';
                                        }}>
                                    <span style={iconStyle}>
                                        <img src={company.logo} alt="" style={{width: '100%', height: '100%', objectFit: 'contain', borderRadius: '50%'}}/>
                                    </span>
                                    <span className="text-lg font-semibold" style={{overflowWrap: 'anywhere'}}>{company.name}</span>
                                    <i className={selectingId === company.id ? 'pi pi-spin pi-spinner' : 'pi pi-arrow-right'}
                                       style={{color: 'var(--primary-color)'}}/>
                                </button>
                            </div>
                        ))}
                    </div>
                </div>
            </PageLayout>
        );
    }

    return (
        <PageLayout header={<h3 className="m-0">{appStore.tr.page_not_found}</h3>}>
            <div className="flex flex-column align-items-center justify-content-center text-center px-3 py-7" style={{minHeight: '55vh'}}>
                <div className="flex align-items-center justify-content-center mb-3" style={iconStyle}>
                    <i className="pi pi-compass" style={{fontSize: '1.8rem'}}/>
                </div>
                <div className="font-bold mb-2" style={{fontSize: '3.5rem', color: 'var(--primary-color)'}}>404</div>
                <h3 className="m-0 mb-4">{appStore.tr.page_not_found}</h3>
                <Btn icon="pi pi-home" label={appStore.tr.home} severity="info" onClick={() => navigate('/')}/>
            </div>
        </PageLayout>
    );
};

export default observer(NotFound);
